import React, { useEffect, useState, useRef } from "react";

interface DiscordOAuthProps {
  onSuccess: (data: {
    email: string;
    username: string;
    discordId: string;
    discordAvatar: string;
  }) => void;
}

const DiscordOAuth: React.FC<DiscordOAuthProps> = ({ onSuccess }) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const processedRef = useRef(false);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const clientId = process.env.REACT_APP_DISCORD_CLIENT_ID;
  const redirectUri = process.env.REACT_APP_DISCORD_REDIRECT_URI;

  const handleDiscordLogin = () => {
    const scope = "identify email";
    const discordAuthUrl = `https://discord.com/api/oauth2/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(
      redirectUri || ""
    )}&response_type=code&scope=${encodeURIComponent(scope)}`;
    window.location.href = discordAuthUrl;
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");

    if (code && !processedRef.current) {
      processedRef.current = true;
      setLoading(true);
      setError(null);

      // Exchange code for user data via backend
      fetch(`${process.env.REACT_APP_API_BASE_URL}/discord/callback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ code, redirectUri: redirectUri }),
      })
        .then(async (response) => {
          if (!response.ok) {
            const text = await response.text();
            throw new Error(text || "Discord authentication failed");
          }
          return response.json();
        })
        .then((data) => {
          // Clear the code from URL after successful exchange
          window.history.replaceState({}, document.title, window.location.pathname);
          setLoading(false);
          onSuccessRef.current({
            email: data.email,
            username: data.username,
            discordId: data.discordId,
            discordAvatar: data.discordAvatar,
          });
        })
        .catch((err) => {
          setLoading(false);
          setError(err.message || "Failed to authenticate with Discord");
          processedRef.current = false; // Allow retry on error
        });
    }
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "2rem" }}>
        <p>Authenticating with Discord...</p>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          marginTop: "1rem",
          alignItems: "center",
        }}
      >
        <button
          onClick={handleDiscordLogin}
          className="discord-btn"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            backgroundColor: "#5865F2",
            color: "white",
            border: "none",
            padding: "12px 24px",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "16px",
            fontWeight: 500,
            width: "100%",
            maxWidth: "300px",
          }}
        >
          <svg
            width="24"
            height="18"
            viewBox="0 -28.5 256 256"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid"
            style={{ flexShrink: 0 }}
          >
            <path
              d="M216.856339,16.5966031 C200.285002,8.84328665 182.566144,3.2084988 164.041564,0 C161.766523,4.11318106 159.108624,9.64549908 157.276099,14.0464379 C137.583995,11.0849896 118.072967,11.0849896 98.7430163,14.0464379 C96.9108417,9.64549908 94.1925838,4.11318106 91.8971895,0 C73.3526068,3.2084988 55.6133949,8.86399117 39.0420583,16.6376612 C5.61752293,67.146514 -3.4433191,116.400813 1.08711069,164.955721 C23.2560196,181.510915 44.7403634,191.567697 65.8621325,198.148576 C71.0772151,190.971126 75.7283628,183.341335 79.7352139,175.300261 C72.104019,172.400575 64.7949724,168.822202 57.8887866,164.667963 C59.7209612,163.310589 61.5131304,161.891452 63.2445898,160.431257 C105.36741,180.133187 151.134928,180.133187 192.754523,160.431257 C194.506336,161.891452 196.298154,163.310589 198.110326,164.667963 C191.183787,168.842556 183.854737,172.420929 176.223542,175.320965 C180.230393,183.341335 184.861538,190.991831 190.096624,198.16893 C211.238746,191.588051 232.743023,181.531619 254.911949,164.955721 C260.227747,108.668201 245.831087,59.8662432 216.856339,16.5966031 Z M85.4738752,135.09489 C72.8290281,135.09489 62.4592217,123.290155 62.4592217,108.914901 C62.4592217,94.5396472 72.607595,82.7145587 85.4738752,82.7145587 C98.3405064,82.7145587 108.709962,94.5189427 108.488529,108.914901 C108.508531,123.290155 98.3405064,135.09489 85.4738752,135.09489 Z M170.525237,135.09489 C157.88039,135.09489 147.510584,123.290155 147.510584,108.914901 C147.510584,94.5396472 157.658606,82.7145587 170.525237,82.7145587 C183.391518,82.7145587 193.761324,94.5189427 193.539891,108.914901 C193.539891,123.290155 183.391518,135.09489 170.525237,135.09489 Z"
              fill="#fff"
              fillRule="nonzero"
            />
          </svg>
          Continue with Discord
        </button>
        {error && <p style={{ color: "red" }}>{error}</p>}
      </div>
    </div>
  );
};

export default DiscordOAuth;
